import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl, defineMessages } from 'react-intl';
import { useHistory } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { Accordion, Container, Icon, Menu, Segment, Message, Button } from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { messages } from '@plone/volto/helpers/MessageLabels/MessageLabels';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import { Form } from '@plone/volto/components/manage/Form';
import { getUser, updateUser } from '@plone/volto/actions/users/users';
import { getUserSchema } from '@plone/volto/actions/userschema/userschema';
import { subscriptionAction } from 'volto-juizi-subscribe/reducers/subscription';
import PeachPaymentCheckout from 'volto-juizi-subscribe/components/PeachPaymentCheckout';
import { getSubscriptions } from 'volto-juizi-subscribe/reducers/subscriptions_data';

const customMessages = defineMessages({
  personalInformation: {
    id: 'Personal Information',
    defaultMessage: 'My Profile',
  },
  subscriptionManagement: {
    id: 'Subscription Management',
    defaultMessage: 'My Subscription',
  },
  activate: {
    id: 'Activate',
    defaultMessage: 'Activate',
  },
  unsubscribe: {
    id: 'Unsubscribe',
    defaultMessage: 'Unsubscribe',
  },
});

const PersonalInformation = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector((state) => state.users.user);
  const userId = useSelector((state) =>
    state.userSession.token ? jwtDecode(state.userSession.token).sub : '',
  );
  const loading = useSelector((state) => state.users.update.loading);
  const loaded = useSelector((state) => state.users.get.loaded);
  const userschema = useSelector((state) => state.userschema);
  const subscriptionState = useSelector((state) => state.subscription);
  const [activeTab, setActiveTab] = useState('personal');
  const [activeAccordion, setActiveAccordion] = useState(0);
  const {
    data: subscriptionsData,
    loaded: subscriptionsLoaded,
    loading: subscriptionsLoading,
    error: subscriptionsError } = useSelector(state => state.subscriptions_data);
  let member_payment_type = {show: false}; 
  const [hasUserInteracted, setHasUserInteracted] = useState(false);


  useEffect(() => {
    // Only fetch if not already loading or loaded
    if (!subscriptionsLoaded) {
      dispatch(getSubscriptions() as any);
    }
  }, [dispatch, subscriptionsLoaded]);

  useEffect(() => {
    if (member_payment_type?.show && !hasUserInteracted) {
      setActiveTab('subscription');
    }
}, [subscriptionsLoaded, member_payment_type, hasUserInteracted]);
  
  useEffect(() => {
    dispatch(getUser(userId));
    dispatch(getUserSchema());
  }, [dispatch, userId]);

  // Watch for subscription action completion
  useEffect(() => {
    if (subscriptionState && subscriptionState.loaded && subscriptionState.result) {
      if (subscriptionState.result.success) {
        toast.success(
          <Toast
            success
            title={intl.formatMessage(messages.success)}
            content={subscriptionState.result.message}
          />,
        );
        // Refresh user data
        dispatch(getUser(userId));
      } else {
        toast.error(
          <Toast
            error
            title="Error"
            content={subscriptionState.result.message || 'Action failed'}
          />,
        );
      }
    }
    if (subscriptionState && subscriptionState.error) {
      toast.error(
        <Toast
          error
          title="Error"
          content={subscriptionState.error.message || 'Action failed'}
        />,
      );
    }
  }, [subscriptionState, dispatch, userId, intl]);
  
  // Auto-subscribe to RiskAlert mailing list once the account is Active
  useEffect(() => {
    if (!loaded || !user) return;

    const expired =
      user.subscription_expiry_date &&
      new Date(user.subscription_expiry_date) < new Date();
    const unsubscribed =
      !expired &&
      user.subscription_date_last_paid !== null &&
      user.peach_registration_id === null;
    const isActive = !expired && !unsubscribed;

    if (!isActive) return;

    // mailing_lists comes from the backend as a JSON string, same as MailingListsWidget expects
    let currentMailingLists = [];
    try {
      if (Array.isArray(user.mailing_lists)) {
        currentMailingLists = user.mailing_lists;
      } else if (typeof user.mailing_lists === 'string' && user.mailing_lists) {
        currentMailingLists = JSON.parse(user.mailing_lists);
      }
    } catch (e) {
      currentMailingLists = [];
    }

    if (!currentMailingLists.includes('RiskAlert')) {
      dispatch(
        updateUser(userId, {
          mailing_lists: JSON.stringify([...currentMailingLists, 'RiskAlert']),
        }),
      );
    }
  }, [loaded, user, dispatch, userId]);

  const onSubmit = (data) => {
    delete data.id;
    delete data.username;
    delete data.roles;
    
    // FIX: Ensure this remains a String (YYYY-MM-DD) to prevent React/Redux crashes
    if (data.subscription_expiry_date) {
      // Create a date object safely
      const dateObj = new Date(data.subscription_expiry_date);

      // Convert back to YYYY-MM-DD string immediately
      // This satisfies the Backend (expects string/date) AND the Frontend Widget (expects string)
      if (!isNaN(dateObj)) {
         data.subscription_expiry_date = dateObj.toISOString().split('T')[0];
      }
    }
    
    dispatch(updateUser(userId, data)).then(() =>
      toast.success(
        <Toast
          success
          title={intl.formatMessage(messages.success)}
          content={intl.formatMessage(messages.saved)}
        />,
      ),
    );
    if (props.closeMenu) props.closeMenu();
  };

  const onCancel = () => {
    if (props.closeMenu) props.closeMenu();
    else history.goBack();
  };

  // Call subscription action using Redux
  const handleSubscriptionAction = (action) => {
    dispatch(subscriptionAction(action));
  };

  // Jump out here if we don't have all the required data
  if (!loaded || !user || !subscriptionsLoaded) {
    return <Container><p>Loading...</p></Container>;
  }

  const personalFields = [
    'fullname',
    'email',
    'home_page',
    'description',
    'location',
    'contact_number',
    'company_name',
    'company_address',
    'company_vat',
  ];

  const subscriptionFields = [
    'subscription_type',
    'subscription_frequency',
    'subscription_amount',
    'subscription_expiry_date',
    'subscription_preferred_payment_day',
    'subscription_date_last_paid',
    'payment_history',
    'mailing_lists',
    'peach_registration_id',
    'subscription_blocked',
    'subscription_payment_gateway',
    'subscription_free_trial',
    'payfast_token',
    'crm_person_id',
  ];

  const personalInfoSchema = userschema?.userschema ? {
    ...userschema.userschema,
    fieldsets: userschema.userschema.fieldsets
      ?.map(fieldset => ({
        ...fieldset,
        fields: fieldset.fields.filter(field => personalFields.includes(field)),
      }))
      .filter(fieldset => fieldset.fields.length > 0),
    properties: Object.fromEntries(
      Object.entries(userschema.userschema.properties || {})
        .filter(([key]) => personalFields.includes(key))
    ),
  } : null;

  const is_expired = (user.subscription_expiry_date && new Date(user.subscription_expiry_date) < new Date());
  const hide_mailing_lists = subscriptionsData.data.hide_mailing_lists_for_unsubscribed;
  // console.log('Is Expired = ', is_expired, '; Hide Mailing Lists = ', hide_mailing_lists);
  const is_unsubscribed = !is_expired && user.subscription_date_last_paid !== null && user.peach_registration_id === null;
  // console.log('Is Unsubscribed: ', is_unsubscribed, ' last: ', user.subscription_date_last_paid, ' reg: ', user.peach_registration_id)
  const is_new_user = user.subscription_date_last_paid === null;
  // console.log('New user: ', is_new_user);

  const buildSubscriptionSchema = () => {
    const backendProperties = userschema?.userschema?.properties || {};
    
    const subscriptionFieldsets = [
      {
        id: 'subscription',
        title: 'Subscription Details',
        fields: [
          'subscription_type',
          'subscription_frequency',
          'subscription_amount',
          'subscription_expiry_date',
          'subscription_date_last_paid',
        ].filter(field => subscriptionFields.includes(field)),
      },
      {
        id: 'invoices',
        title: 'Invoices',
        fields: [
          'payment_history',
        ].filter(field => subscriptionFields.includes(field)),
      },
      {
        id: 'mailing_lists',
        title: 'Mailing Lists',
        fields: [
          'mailing_lists',
        ].filter(field => !hide_mailing_lists && subscriptionFields.includes(field)),
      },
      {
        id: 'hidden',
        title: 'Hidden',
        fields: [
          'subscription_payment_gateway',
          'peach_registration_id',
          'subscription_blocked',
          'subscription_free_trial',
          'payfast_token',
          'crm_person_id',
        ].filter(field => false && subscriptionFields.includes(field)),
      }
    ].filter(fieldset => fieldset.fields.length > 0);

    const properties = {};
    subscriptionFields.forEach(fieldName => {
      if (backendProperties[fieldName]) {
        properties[fieldName] = backendProperties[fieldName];
        // Disable all fields
        properties[fieldName].isDisabled=true;
        // console.log('fieldName: ', fieldName, ' props ', backendProperties[fieldName]);
        if (fieldName === 'payment_history') {
          properties[fieldName].type = 'array';
          properties[fieldName].widget = 'payment_history_widget';
          properties[fieldName].items = {
            type: 'object',
            properties: {
              date: { type: 'string' },
              amount: { type: 'string' },
            },
          };
        }
        if (fieldName === 'mailing_lists') {
          properties[fieldName].type = 'list';
          properties[fieldName].widget = 'mailing_lists_widget';
        };
      }
    });

    return {
      fieldsets: subscriptionFieldsets,
      properties: properties,
      required: [],
    };
  };

  const subscriptionSchema = buildSubscriptionSchema();
  // console.log("is_expired: ", is_expired, " date: ", new Date(user.subscription_expiry_date), " today: ", new Date());
  if (user.subscription_type) {
    let member_payment_types = subscriptionsData.data.member_payment_types.filter(
      type => type.member_type === user.subscription_type && type.payment_type === user.subscription_frequency);
    if (member_payment_types) {
      member_payment_type = member_payment_types[0]
    }
  }

  // console.log('Got subscriptionsData: ', subscriptionsData);
  return (
    <div id="page-personal-information">
      <Helmet title={intl.formatMessage(customMessages.personalInformation)} />
      <Container>
        <article id="content">
          <header>
            <h1 className="documentFirstHeading">
              {intl.formatMessage(customMessages.personalInformation)}
            </h1>
          </header>

          <Menu attached="top" tabular>
            <Menu.Item
              name="personal"
              active={activeTab === 'personal'}
              onClick={() => {
                setHasUserInteracted(true);
                setActiveTab('personal')
              }}
            >
              {intl.formatMessage(customMessages.personalInformation)}
            </Menu.Item>
            {member_payment_type.show && (
              <Menu.Item
                name="subscription"
                active={activeTab === 'subscription'}
                onClick={() => {
                  setHasUserInteracted(true);
                  setActiveTab('subscription')
                }}
              >
                {intl.formatMessage(customMessages.subscriptionManagement)}
              </Menu.Item>
            )}
          </Menu>

          <Segment attached="bottom">
            {activeTab === 'personal' && personalInfoSchema && (
              <div>
                <Segment>
                  <Button className="primary button" as={Link} to="/passwordreset">Reset your password</Button>
                </Segment>
                <Form
                  schema={personalInfoSchema}
                  formData={user}
                  onSubmit={onSubmit}
                  onCancel={onCancel}
                  loading={loading}
                />
              </div>
            )}

            {member_payment_type.show && activeTab === 'subscription' && (
              <div>
                <div className="subscription-status-actions">
                  <div className={`subscription-status${is_unsubscribed ? ' subscription-status--full-width' : ''}`}>
                    {user?.subscription_blocked && (
                      <Message negative className="subscription-status-message">
                        <strong>Subscription Blocked: </strong>
                        <span>Your subscription is currently blocked. Please contact support.</span>
                      </Message>
                    )}
                    {user?.subscription_expiry_date && (
                      <Message
                        className="subscription-status-message"
                        info={is_expired == false}
                        warning={is_expired}
                      >
                        <strong>Subscription Status: </strong>
                        <span>
                          {is_expired
                            ? (is_new_user
                                ? 'Awaiting activation'
                                : `Expired on ${new Date(user.subscription_expiry_date).toLocaleDateString()}`
                              )
                            : is_unsubscribed
                              ? `Cancelled. Active until ${new Date(user.subscription_expiry_date).toLocaleDateString()}`
                              : `Active. Next payment on ${new Date(user.subscription_expiry_date).toLocaleDateString()}`
                          }
                        </span>
                      </Message>
                    )}
                  </div>

                  {!is_unsubscribed && (
                    <div className="subscription-actions">
                      {is_expired
                        ? <PeachPaymentCheckout
                            amount={parseFloat(member_payment_type?.amount) || 0}
                            currency="ZAR"
                            subscriptionType={user?.subscription_type || 'Individual'}
                            onPaymentSuccess={(payment) => {
                              dispatch(getUser(userId));
                            }}
                            onPaymentError={(error) => {
                              console.error('Payment error:', error);
                            }}
                          />
                        : <Button
                            basic
                            negative
                            onClick={() => handleSubscriptionAction('unsubscribe')}
                            loading={subscriptionState.loading}
                            disabled={subscriptionState.loading}
                          >
                            {intl.formatMessage(customMessages.unsubscribe)}
                          </Button>
                      }
                    </div>
                  )}
                </div>

                <div className="subscription-details">
                  <Accordion fluid styled>
                    {subscriptionSchema.fieldsets.map((fieldset, index) => {
                      const isFirst = index === 0;
                      const isLast = index === subscriptionSchema.fieldsets.length - 1;
                      const panelSchema = {
                        fieldsets: [fieldset],
                        properties: subscriptionSchema.properties,
                        required: [],
                      };
                      return (
                        <React.Fragment key={fieldset.id}>
                          <Accordion.Title
                            active={activeAccordion === index}
                            onClick={() => setActiveAccordion(activeAccordion === index ? -1 : index)}
                            className={`${isFirst ? 'accordion-title--first' : ''}${isLast ? ' accordion-title--last' : ''}`.trim()}
                          >
                            <Icon name="dropdown" />
                            {fieldset.title}
                          </Accordion.Title>
                          <Accordion.Content active={activeAccordion === index} className={`accordion-content--${fieldset.id}`}>
                            <Form
                              schema={panelSchema}
                              formData={user}
                              onSubmit={onSubmit}
                              onCancel={onCancel}
                              loading={subscriptionsLoading}
                            />
                          </Accordion.Content>
                        </React.Fragment>
                      );
                    })}
                  </Accordion>
                </div>
              </div>
            )}
          </Segment>

        </article>
      </Container>
    </div>
  );
};

PersonalInformation.propTypes = {
  closeMenu: PropTypes.func,
};

export default PersonalInformation;
